import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/* Aanbevolen producten in de winkelwagen, met maatkeuze en direct toevoegen. */

const money = (cents) => '€' + (cents / 100).toFixed(2).replace('.', ',');
const toCents = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'string' && v.includes('.')) return Math.round(parseFloat(v) * 100);
  return Number(v);
};
const esc = (s) =>
  String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const isSize = (name) => /maat|size|schoenmaat/i.test(name || '');
const isColor = (name) => /kleur|color|colour/i.test(name || '');
const gender = (t) => (/dames/i.test(t) ? 'd' : /heren/i.test(t) ? 'h' : '');

const ICON_ADD =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
const ICON_OK =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

class DgsCartUpsell extends HTMLElement {
  #firstId = null;
  #products = [];
  #cart = null;
  #busy = false;

  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.querySelectorAll('.dgs-up__arrow').forEach((b) =>
      b.addEventListener('click', () => {
        const card = this.track.querySelector('.dgs-up__card');
        const step = card ? card.getBoundingClientRect().width + 10 : 180;
        this.track.scrollBy({ left: step * Number(b.dataset.dir), behavior: 'smooth' });
      })
    );
    this.track.addEventListener('scroll', () => this.#syncArrows(), { passive: true });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#refresh();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = (event) => {
    event.promise
      ?.then(() => setTimeout(() => this.#refresh(), this.#busy ? 900 : 0))
      .catch(() => {});
  };

  async #refresh() {
    try {
      this.#cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const items = this.#cart.items || [];
      if (!items.length) {
        this.hidden = true;
        return;
      }
      const firstId = items[0].product_id;
      if (firstId !== this.#firstId || !this.#products.length) {
        this.#firstId = firstId;
        this.#products = await this.#load(firstId, items[0].product_title || '');
      }
      this.#render();
    } catch (e) {
      console.warn('[dgs-upsell]', e);
    }
  }

  async #load(productId, firstTitle) {
    let list = [];
    try {
      const r = await fetch(`/recommendations/products.json?product_id=${productId}&limit=10&intent=related`);
      if (r.ok) list = (await r.json()).products || [];
    } catch (e) {}
    if (list.length < 3) {
      try {
        const r = await fetch('/collections/nieuw-binnen/products.json?limit=16');
        if (r.ok) {
          const extra = (await r.json()).products || [];
          const seen = new Set(list.map((p) => p.id));
          extra.forEach((p) => {
            if (!seen.has(p.id) && p.id !== productId) list.push(p);
          });
        }
      } catch (e) {}
    }
    const g = gender(firstTitle);
    if (g) {
      const same = list.filter((p) => gender(p.title) === g || !gender(p.title));
      if (same.length >= 2) list = same;
      list.sort((a, b) => (gender(b.title) === g) - (gender(a.title) === g));
    }
    return list.map((p) => this.#normalize(p)).filter(Boolean);
  }

  #normalize(p) {
    const opts = (p.options || []).map((o) => (typeof o === 'string' ? o : o.name));
    const sizeIdx = opts.findIndex(isSize);
    const colorIdx = opts.findIndex(isColor);
    const base = (u) => String(u || '').split('?')[0].replace(/^https?:/, '');
    const vImg = (v) => (v.featured_image && (v.featured_image.src || v.featured_image)) || '';
    const variants = (p.variants || []).map((v) => ({
      id: v.id,
      available: v.available !== false,
      price: toCents(v.price),
      compare: toCents(v.compare_at_price),
      opts: [v.option1, v.option2, v.option3],
      img: typeof vImg(v) === 'string' ? vImg(v) : '',
    }));
    let pImg = p.featured_image || (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
    if (typeof pImg === 'object' && pImg) pImg = pImg.src || '';
    const avail = variants.filter((v) => v.available);
    if (!avail.length) return null;
    const first = avail.find((v) => v.img && base(v.img) === base(pImg)) || avail[0];
    const color = colorIdx > -1 ? first.opts[colorIdx] : null;
    const group = color != null ? variants.filter((v) => v.opts[colorIdx] === color) : variants;
    let img = first.img || pImg;
    if (img && img.startsWith('//')) img = 'https:' + img;
    if (img) img += (img.includes('?') ? '&' : '?') + 'width=360';
    return {
      id: p.id,
      title: p.title,
      url: p.url || `/products/${p.handle}`,
      img,
      sizeIdx,
      single: variants.length === 1,
      firstId: first.id,
      price: first.price,
      compare: first.compare,
      sizes: sizeIdx > -1 ? group.map((v) => ({ id: v.id, label: v.opts[sizeIdx], available: v.available })) : [],
    };
  }

  #cartSize() {
    const it = this.#cart?.items?.[0];
    const o = (it?.options_with_values || []).find((x) => isSize(x.name));
    return o ? String(o.value) : null;
  }

  #render() {
    const inCart = new Set((this.#cart.items || []).map((i) => i.product_id));
    const list = this.#products.filter((p) => !inCart.has(p.id)).slice(0, 6);
    if (!list.length) {
      this.hidden = true;
      return;
    }
    const pre = this.#cartSize();
    this.track.innerHTML = list
      .map((p) => {
        const pct = p.compare > p.price ? Math.round((1 - p.price / p.compare) * 100) : 0;
        let buy;
        if (p.sizes.length) {
          const hasPre = p.sizes.some((s) => s.available && String(s.label) === pre);
          buy = `<select class="dgs-up__select" aria-label="Kies uw maat voor ${esc(p.title)}">
            <option value="">Maat</option>
            ${p.sizes
              .map(
                (s) =>
                  `<option value="${s.id}"${!s.available ? ' disabled' : ''}${hasPre && String(s.label) === pre ? ' selected' : ''}>Maat ${esc(s.label)}${!s.available ? ' (uitverkocht)' : ''}</option>`
              )
              .join('')}
          </select>`;
        } else {
          buy = `<input type="hidden" class="dgs-up__select" value="${p.firstId}">`;
        }
        return `<li class="dgs-up__card">
          <a class="dgs-up__img" href="${esc(p.url)}">
            ${p.img ? `<img src="${esc(p.img)}" alt="${esc(p.title)}" loading="lazy" width="180" height="180">` : ''}
            ${pct > 0 ? `<span class="dgs-up__pct">-${pct}%</span>` : ''}
          </a>
          <div class="dgs-up__body">
            <a class="dgs-up__name" href="${esc(p.url)}">${esc(p.title)}</a>
            <div class="dgs-up__price${pct > 0 ? '' : ' is-regular'}">${money(p.price)}${pct > 0 ? `<s>${money(p.compare)}</s>` : ''}</div>
            <div class="dgs-up__buy">${buy}
              <button type="button" class="dgs-up__add" aria-label="${esc(p.title)} in winkelwagen">${ICON_ADD}</button>
            </div>
            <p class="dgs-up__msg" role="alert"></p>
          </div>
        </li>`;
      })
      .join('');
    this.hidden = false;
    requestAnimationFrame(() => this.#syncArrows());
  }

  #syncArrows() {
    const [prev, next] = this.querySelectorAll('.dgs-up__arrow');
    if (!prev || !next) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    prev.disabled = this.track.scrollLeft <= 2;
    next.disabled = this.track.scrollLeft >= max;
  }

  #onChange = (e) => {
    if (e.target.classList.contains('dgs-up__select')) {
      e.target.classList.remove('is-error');
      const msg = e.target.closest('.dgs-up__card')?.querySelector('.dgs-up__msg');
      if (msg) msg.textContent = '';
    }
  };

  #onClick = (e) => {
    const btn = e.target.closest('.dgs-up__add');
    if (!btn) return;
    const card = btn.closest('.dgs-up__card');
    const sel = card.querySelector('.dgs-up__select');
    const msg = card.querySelector('.dgs-up__msg');
    if (!sel.value) {
      sel.classList.add('is-error');
      msg.textContent = 'Kies eerst uw maat';
      sel.focus();
      return;
    }
    this.#add(Number(sel.value), btn, msg);
  };

  async #add(variantId, btn, msg) {
    this.#busy = true;
    btn.classList.add('is-busy');
    const sections = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => el.dataset.sectionId && sections.add(el.dataset.sectionId));
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: Array.from(sections).join(','),
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      if (res.status) {
        msg.textContent = res.status === 422 ? 'Deze maat is helaas uitverkocht' : 'Dat lukte niet, probeer het opnieuw';
        btn.classList.remove('is-busy');
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'dgs-cart-upsell', itemCount: 0 },
        });
        this.#busy = false;
        return;
      }
      btn.classList.remove('is-busy');
      btn.classList.add('is-added');
      btn.innerHTML = ICON_OK;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: res.sections,
          itemCount: 1,
          source: 'dgs-cart-upsell',
          didError: false,
        },
      });
    } catch (err) {
      btn.classList.remove('is-busy');
      msg.textContent = 'Dat lukte niet, probeer het opnieuw';
      deferred.reject(err);
    }
    setTimeout(() => (this.#busy = false), 1000);
  }
}

if (!customElements.get('dgs-cart-upsell')) customElements.define('dgs-cart-upsell', DgsCartUpsell);

/* Productpagina (dgs-product): na "In winkelmand" de winkelwagen bijwerken en openen,
   in plaats van de oude melding "Toegevoegd aan uw winkelmand". */
(function () {
  try {
    const st = document.createElement('style');
    st.textContent = '.dgs-pdp__added{display:none!important}';
    document.head.appendChild(st);
  } catch (e) {}
  document.addEventListener('cart:update', async (e) => {
    if (!e.detail || e.detail.source !== 'dgs-product') return;
    document.querySelectorAll('.dgs-pdp__added').forEach((el) => (el.hidden = true));
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const first = cart.items && cart.items[0];
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: first ? [{ merchandiseId: String(first.variant_id), quantity: 1 }] : [],
          promise: deferred.promise,
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: 1, source: 'dgs-product', didError: false },
      });
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function') setTimeout(() => drawer.open(), 200);
    } catch (err) {
      window.location.reload();
    }
  });
})();
